import { useQuery } from '@tanstack/react-query';
import { companyApi } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { downloadSignedUrl } from '@l2u/ui/lib/download.ts';
import { meKeys } from '../lib/meQueryKeys.ts';

// Preview a vendor file in a modal: images inline, PDFs in an iframe, and an
// open-in-new-tab fallback for anything else. Mirrors the admin
// FilePreviewModal but against the vendor's /api/me endpoints. Shared by the
// Documents tab and the questionnaire's Review step.
//
// cacheKey names the signed URL in the query cache. Both screens key an
// uploaded file as `file:<id>`, so opening the same file from either one
// reuses a URL that is still fresh.
export function FilePreviewModal({
  fileName,
  mimeType,
  downloadPath,
  cacheKey,
  onClose,
}: {
  fileName: string;
  mimeType: string;
  /** GET route that returns `{ url }`, a short-lived signed storage URL. */
  downloadPath: string;
  cacheKey: string;
  onClose: () => void;
}) {
  const urlQuery = useQuery({
    queryKey: meKeys.filePreview(cacheKey),
    queryFn: () => companyApi.get<{ url: string }>(downloadPath),
    staleTime: 4 * 60 * 1000,
  });
  const url = urlQuery.data?.url;
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';

  function openInNewTab() {
    if (url) window.open(url, '_blank', 'noopener');
  }

  return (
    <Modal
      open
      title={fileName}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Btn
            size="sm"
            variant="ghost"
            disabled={!url}
            onClick={() => url && downloadSignedUrl(url, fileName)}
          >
            Download file
          </Btn>
          <Btn size="sm" variant="ghost" disabled={!url} onClick={openInNewTab}>
            Open in new tab
          </Btn>
          <Btn size="sm" variant="secondary" onClick={onClose}>
            Close
          </Btn>
        </>
      }
    >
      {!url ? (
        <div className="flex h-40 items-center justify-center text-sm text-slate-400">
          {urlQuery.isError ? 'File unavailable' : 'Loading…'}
        </div>
      ) : isImage ? (
        <img src={url} alt={fileName} className="mx-auto max-h-[65vh] max-w-full rounded-md" />
      ) : isPdf ? (
        <iframe
          src={url}
          title={fileName}
          className="h-[65vh] w-full rounded-md border border-slate-200"
        />
      ) : (
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-500">
          <span>Preview not available for this file type.</span>
          <Btn size="sm" variant="secondary" onClick={openInNewTab}>
            Open in new tab
          </Btn>
        </div>
      )}
    </Modal>
  );
}
